import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { IVoxelKafes } from './IDokumSim';

/**
 * Parçayı (döküm kavitesi) voxelle.
 * İç/dış testi: en yakın yüzey noktası + yüzey normali işareti (signed distance) —
 * ışın-parity'den çok daha sağlam, su geçirmez mesh gerektirmez (STEP açık tepsi olsa bile).
 * Kalınlık: 2 × (en yakın yüzeye mesafe) = içteki noktanın çevreleyen küre çapı ≈ yerel kesit
 * (medial-axis kalınlığı — ışın-karşı-duvar yönteminden çok daha doğru).
 */

export interface IVoxellemeGirdi {
  positions: Float32Array;   // dünya uzayı
  indices: Uint32Array;
  bbox: THREE.Box3;
  /** Hedef en uzun eksendeki hücre sayısı (çözünürlük). Varsayılan 60. */
  cozunurluk?: number;
  /** Kesin parça hacmi (cm³) — nativeVolumeCm3; verilirse hacim buna sabitlenir */
  hacimCm3Kesin?: number;
  /** İlerleme 0-100 */
  ilerle?: (y: number) => void;
}

const MAX_HUCRE = 2500000; // scanline yöntemi ucuz → ince voxel (~2-3mm)

export function voxelle(g: IVoxellemeGirdi): IVoxelKafes {
  const ilerle = g.ilerle ?? (() => {});
  const boyutVec = g.bbox.getSize(new THREE.Vector3());
  const enUzun = Math.max(boyutVec.x, boyutVec.y, boyutVec.z) || 1;
  const coz = Math.max(16, Math.min(g.cozunurluk ?? 60, 160));

  let hucreMm = enUzun / coz;
  // Hücre sayısı tavanı
  const hesapBoyut = () => [
    Math.max(1, Math.ceil(boyutVec.x / hucreMm)),
    Math.max(1, Math.ceil(boyutVec.y / hucreMm)),
    Math.max(1, Math.ceil(boyutVec.z / hucreMm)),
  ] as [number, number, number];
  let [nx, ny, nz] = hesapBoyut();
  while (nx * ny * nz > MAX_HUCRE) { hucreMm *= 1.18; [nx, ny, nz] = hesapBoyut(); }

  // BVH
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(g.positions, 3));
  geom.setIndex(new THREE.BufferAttribute(g.indices, 1));
  const bvh = new MeshBVH(geom);

  const toplam = nx * ny * nz;
  const doluluk = new Uint8Array(toplam);
  const kalinlik = new Float32Array(toplam);
  const origin = g.bbox.min.clone();

  const merkez = new THREE.Vector3();
  const hedef = { point: new THREE.Vector3(), distance: 0, faceIndex: -1 };
  let doluSayi = 0;
  const ray = new THREE.Ray();
  const nxy = nx * ny;
  const oy = new Uint8Array(toplam); // 3 eksen oy toplamı
  const maxUz = boyutVec.length() * 1.2;

  // ── Scanline parity (3 eksen). Grid-çizgisi başına TEK ışın → ucuz, ince voxel.
  // Eksen boyunca yüzey kesişimleri sıralı; her kesişim iç/dış'ı çevirir (parity).
  // X ekseni: her (j,k) için bir ışın
  for (let k = 0; k < nz; k++) {
    const z = origin.z + (k + 0.5) * hucreMm;
    for (let j = 0; j < ny; j++) {
      const y = origin.y + (j + 0.5) * hucreMm;
      ray.origin.set(origin.x - hucreMm, y, z);
      ray.direction.set(1, 0, 0);
      const hits = bvh.raycast(ray, THREE.DoubleSide as any, 0, maxUz + hucreMm);
      if (!hits || hits.length === 0) continue;
      hits.sort((a: any, b: any) => a.point.x - b.point.x);
      let h = 0, ic = false;
      const taban = nx * (j + ny * k);
      for (let i = 0; i < nx; i++) {
        const xc = origin.x + (i + 0.5) * hucreMm;
        while (h < hits.length && hits[h].point.x < xc) { ic = !ic; h++; }
        if (ic) oy[i + taban]++;
      }
    }
    if ((k & 3) === 0) ilerle((k / nz) * 40);
  }
  // Y ekseni: her (i,k)
  for (let k = 0; k < nz; k++) {
    const z = origin.z + (k + 0.5) * hucreMm;
    for (let i = 0; i < nx; i++) {
      const x = origin.x + (i + 0.5) * hucreMm;
      ray.origin.set(x, origin.y - hucreMm, z);
      ray.direction.set(0, 1, 0);
      const hits = bvh.raycast(ray, THREE.DoubleSide as any, 0, maxUz + hucreMm);
      if (!hits || hits.length === 0) continue;
      hits.sort((a: any, b: any) => a.point.y - b.point.y);
      let h = 0, ic = false;
      for (let j = 0; j < ny; j++) {
        const yc = origin.y + (j + 0.5) * hucreMm;
        while (h < hits.length && hits[h].point.y < yc) { ic = !ic; h++; }
        if (ic) oy[i + nx * (j + ny * k)]++;
      }
    }
    if ((k & 3) === 0) ilerle(40 + (k / nz) * 25);
  }
  // Z ekseni: her (i,j)
  for (let j = 0; j < ny; j++) {
    const y = origin.y + (j + 0.5) * hucreMm;
    for (let i = 0; i < nx; i++) {
      const x = origin.x + (i + 0.5) * hucreMm;
      ray.origin.set(x, y, origin.z - hucreMm);
      ray.direction.set(0, 0, 1);
      const hits = bvh.raycast(ray, THREE.DoubleSide as any, 0, maxUz + hucreMm);
      if (!hits || hits.length === 0) continue;
      hits.sort((a: any, b: any) => a.point.z - b.point.z);
      let h = 0, ic = false;
      for (let k = 0; k < nz; k++) {
        const zc = origin.z + (k + 0.5) * hucreMm;
        while (h < hits.length && hits[h].point.z < zc) { ic = !ic; h++; }
        if (ic) oy[i + nx * j + nxy * k]++;
      }
    }
    if ((j & 3) === 0) ilerle(65 + (j / ny) * 15);
  }

  // Çoğunluk (≥2/3) = içeride; kalınlığı içeridekiler için SDF'ten
  for (let lin = 0; lin < toplam; lin++) {
    if (oy[lin] >= 2) {
      doluluk[lin] = 1;
      merkez.set(
        origin.x + ((lin % nx) + 0.5) * hucreMm,
        origin.y + ((Math.floor(lin / nx) % ny) + 0.5) * hucreMm,
        origin.z + (Math.floor(lin / nxy) + 0.5) * hucreMm,
      );
      const cp: any = bvh.closestPointToPoint(merkez, hedef as any);
      kalinlik[lin] = cp ? Math.max(hucreMm, cp.distance * 2) : hucreMm;
      doluSayi++;
    }
  }
  ilerle(95);

  geom.dispose();

  // En büyük bağlı bileşeni tut — açık/su geçirmez olmayan parçada iç/dış testi
  // köşelerde yanlış pozitif verip kopuk dış bloklar üretir; onları temizle.
  doluSayi = enBuyukBileseniTut(doluluk, kalinlik, nx, ny, nz);

  const voxelHacimCm3 = (doluSayi * hucreMm * hucreMm * hucreMm) / 1000;
  const hacimCm3 = (g.hacimCm3Kesin && g.hacimCm3Kesin > 0) ? g.hacimCm3Kesin : voxelHacimCm3;

  return { boyut: [nx, ny, nz], hucreMm, origin, doluluk, doluSayi, kalinlik, hacimCm3 };
}

/** Besleyici damgalama girdisi (dünya uzayı silindir) */
export interface IKafesBesleyici {
  pozisyon: THREE.Vector3;   // silindir taban merkezi (parça yüzeyinde)
  yon: THREE.Vector3;        // eksen yönü (genelde yukarı)
  capMm: number;
  yukseklikMm: number;
}

/**
 * Kafesi besleyici (riser) silindirlerini kapsayacak şekilde GENİŞLET ve
 * silindirleri gerçek voxel hacmi olarak damgala. Böylece besleyiciler dolum
 * CA'sına + katılaşma FDM'ine + çekme/besleme muhasebesine fiilen katılır
 * (MAGMA'daki gibi: riser da termal alanın parçası, pipe oluşumu görünür).
 *
 * bolgeTipi: 0 = parça, 1 = besleyici. besleyiciIdx: silindir sırası (-1 = değil).
 * kalinlik (besleyici voxeli) = besleyici çapı (büyük modül → geç katılaşır).
 * hacimCm3: parça + besleyici hacmi (dolum süresi ölçeği doğru kalsın).
 */
export function kafesGenislet(k: IVoxelKafes, besleyiciler: IKafesBesleyici[]): IVoxelKafes {
  if (!besleyiciler.length) return k;
  const h = k.hucreMm;
  const [nx, ny, nz] = k.boyut;

  // Genişletilmiş sınır kutusu: mevcut grid + tüm silindir uçları (+1 hücre pay)
  const min = k.origin.clone();
  const max = new THREE.Vector3(
    k.origin.x + nx * h, k.origin.y + ny * h, k.origin.z + nz * h,
  );
  const uc = new THREE.Vector3();
  for (const b of besleyiciler) {
    const r = Math.max(b.capMm * 0.5, h);
    const yon = b.yon.lengthSq() > 1e-6 ? b.yon.clone().normalize() : new THREE.Vector3(0, 1, 0);
    for (const t of [0, b.yukseklikMm]) {
      uc.copy(b.pozisyon).addScaledVector(yon, t);
      min.x = Math.min(min.x, uc.x - r); min.y = Math.min(min.y, uc.y - r); min.z = Math.min(min.z, uc.z - r);
      max.x = Math.max(max.x, uc.x + r); max.y = Math.max(max.y, uc.y + r); max.z = Math.max(max.z, uc.z + r);
    }
  }
  // Yeni grid boyutları (origin'i hücre ızgarasına hizala — eski voxeller birebir kopyalanır)
  const ox = Math.floor((min.x - k.origin.x) / h);
  const oy = Math.floor((min.y - k.origin.y) / h);
  const oz = Math.floor((min.z - k.origin.z) / h);
  const yeniOrigin = new THREE.Vector3(
    k.origin.x + ox * h, k.origin.y + oy * h, k.origin.z + oz * h,
  );
  const mx = Math.max(nx - ox, Math.ceil((max.x - yeniOrigin.x) / h));
  const my = Math.max(ny - oy, Math.ceil((max.y - yeniOrigin.y) / h));
  const mz = Math.max(nz - oz, Math.ceil((max.z - yeniOrigin.z) / h));
  const mToplam = mx * my * mz;

  const doluluk = new Uint8Array(mToplam);
  const kalinlik = new Float32Array(mToplam);
  const bolgeTipi = new Uint8Array(mToplam);
  const besleyiciIdx = new Int16Array(mToplam).fill(-1);

  // Eski parça voxellerini kopyala (ofsetli)
  const dx = -ox, dy = -oy, dz = -oz; // eski → yeni indis kayması
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      const eskiSatir = nx * (y + ny * z);
      const yeniSatir = mx * ((y + dy) + my * (z + dz));
      for (let x = 0; x < nx; x++) {
        const e = eskiSatir + x;
        if (k.doluluk[e] !== 1) continue;
        const yn = yeniSatir + (x + dx);
        doluluk[yn] = 1;
        kalinlik[yn] = k.kalinlik[e];
      }
    }
  }

  // Besleyici silindirlerini damgala
  let besleyiciVoxel = 0;
  const p = new THREE.Vector3();
  for (let bi = 0; bi < besleyiciler.length; bi++) {
    const b = besleyiciler[bi];
    const yon = b.yon.lengthSq() > 1e-6 ? b.yon.clone().normalize() : new THREE.Vector3(0, 1, 0);
    const r = Math.max(b.capMm * 0.5, h * 0.6);
    const r2 = r * r;
    const H = Math.max(b.yukseklikMm, h);
    // Silindir AABB'si içinde tara
    const abox = new THREE.Box3().setFromPoints([
      b.pozisyon.clone().addScaledVector(yon, 0).addScalar(-r),
      b.pozisyon.clone().addScaledVector(yon, 0).addScalar(r),
      b.pozisyon.clone().addScaledVector(yon, H).addScalar(-r),
      b.pozisyon.clone().addScaledVector(yon, H).addScalar(r),
    ]);
    const x0 = Math.max(0, Math.floor((abox.min.x - yeniOrigin.x) / h));
    const x1 = Math.min(mx - 1, Math.ceil((abox.max.x - yeniOrigin.x) / h));
    const y0 = Math.max(0, Math.floor((abox.min.y - yeniOrigin.y) / h));
    const y1 = Math.min(my - 1, Math.ceil((abox.max.y - yeniOrigin.y) / h));
    const z0 = Math.max(0, Math.floor((abox.min.z - yeniOrigin.z) / h));
    const z1 = Math.min(mz - 1, Math.ceil((abox.max.z - yeniOrigin.z) / h));
    for (let z = z0; z <= z1; z++) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          p.set(
            yeniOrigin.x + (x + 0.5) * h,
            yeniOrigin.y + (y + 0.5) * h,
            yeniOrigin.z + (z + 0.5) * h,
          ).sub(b.pozisyon);
          const t = p.dot(yon);                       // eksen üzerindeki izdüşüm
          if (t < -h * 0.5 || t > H) continue;
          const d2 = p.lengthSq() - t * t;            // eksene dik mesafe²
          if (d2 > r2) continue;
          const lin = x + mx * (y + my * z);
          if (doluluk[lin] === 1 && bolgeTipi[lin] === 0) continue; // parça önce gelir
          if (doluluk[lin] !== 1) besleyiciVoxel++;
          doluluk[lin] = 1;
          bolgeTipi[lin] = 1;
          besleyiciIdx[lin] = bi;
          if (b.capMm > kalinlik[lin]) kalinlik[lin] = b.capMm;
        }
      }
    }
  }

  let doluSayi = 0;
  for (let i = 0; i < mToplam; i++) if (doluluk[i] === 1) doluSayi++;
  const besHacimCm3 = (besleyiciVoxel * h * h * h) / 1000;

  return {
    boyut: [mx, my, mz],
    hucreMm: h,
    origin: yeniOrigin,
    doluluk,
    doluSayi,
    kalinlik,
    hacimCm3: k.hacimCm3 + besHacimCm3,
    bolgeTipi,
    besleyiciIdx,
  };
}

/** 6-bağ bağlı bileşenlerden en büyüğünü koru, diğer dolu hücreleri sıfırla. Yeni dolu sayısı döner. */
function enBuyukBileseniTut(doluluk: Uint8Array, kalinlik: Float32Array, nx: number, ny: number, nz: number): number {
  const N = doluluk.length;
  const etiket = new Int32Array(N).fill(-1);
  const stack = new Int32Array(N);
  let enBuyukEtiket = -1, enBuyukSay = 0, etiketSay = 0;
  const nxy = nx * ny;

  for (let s = 0; s < N; s++) {
    if (doluluk[s] !== 1 || etiket[s] !== -1) continue;
    const e = etiketSay++;
    let sp = 0, say = 0;
    stack[sp++] = s; etiket[s] = e;
    while (sp > 0) {
      const v = stack[--sp]; say++;
      const ix = v % nx, iy = Math.floor(v / nx) % ny, iz = Math.floor(v / nxy);
      if (ix > 0)      { const k = v - 1;   if (doluluk[k] === 1 && etiket[k] === -1) { etiket[k] = e; stack[sp++] = k; } }
      if (ix < nx - 1) { const k = v + 1;   if (doluluk[k] === 1 && etiket[k] === -1) { etiket[k] = e; stack[sp++] = k; } }
      if (iy > 0)      { const k = v - nx;  if (doluluk[k] === 1 && etiket[k] === -1) { etiket[k] = e; stack[sp++] = k; } }
      if (iy < ny - 1) { const k = v + nx;  if (doluluk[k] === 1 && etiket[k] === -1) { etiket[k] = e; stack[sp++] = k; } }
      if (iz > 0)      { const k = v - nxy; if (doluluk[k] === 1 && etiket[k] === -1) { etiket[k] = e; stack[sp++] = k; } }
      if (iz < nz - 1) { const k = v + nxy; if (doluluk[k] === 1 && etiket[k] === -1) { etiket[k] = e; stack[sp++] = k; } }
    }
    if (say > enBuyukSay) { enBuyukSay = say; enBuyukEtiket = e; }
  }

  if (enBuyukEtiket < 0) return 0;
  let yeniSay = 0;
  for (let v = 0; v < N; v++) {
    if (doluluk[v] === 1 && etiket[v] === enBuyukEtiket) yeniSay++;
    else if (doluluk[v] === 1) { doluluk[v] = 0; kalinlik[v] = 0; }
  }
  return yeniSay;
}
